import { EdgeProps, useReactFlow } from '@xyflow/react';
import { rootStore } from '../stores/UnifiedStoreProvider';
import { v4 as uuidv4 } from 'uuid';

// this hook implements the logic for clicking the button on a workflow edge
// on edge click: create a node in between the two nodes that are connected by the edge
function useEdgeClick(edgeId: EdgeProps['id']) {
  const { setEdges, setNodes, getNode, getEdge, setCenter } = useReactFlow();

  const handleEdgeClick = async () => {
    console.log('Edge clicked:', edgeId);
    
    // first we retrieve the edge object to get the source and target id
    const edge = getEdge(edgeId);

    if (!edge) {
      console.log('Edge not found:', edgeId);
      return;
    }

    // we retrieve the source and target nodes to get their positions
    const sourceNode = getNode(edge.source);
    const targetNode = getNode(edge.target);

    if (!sourceNode || !targetNode) {
      console.log('Source or target node not found');
      return;
    }

    // create a unique id for the new node
    const newNodeId = uuidv4();

    // Calculate position for the new node (midpoint between source and target)
    const position = {
      x: (sourceNode.position.x + targetNode.position.x) / 2,
      y: (sourceNode.position.y + targetNode.position.y) / 2
    };

    // Use blank label for new node (user will fill it in)
    const label = '';

    try {
      // Create the new node using MobX store with optimistic update
      if (rootStore.mindmapStore) {
        await rootStore.mindmapStore.createNodeOptimistically(edge.source);
      }

      console.log('Successfully inserted node between nodes');
      
      // Update the React Flow UI immediately (while DB sync happens in background)
      // Create the new node for React Flow
      const newNode = {
        id: newNodeId,
        type: 'workflow',
        position: position,
        data: { 
          label: label,
          priority: 'medium',
          isNecessary: true,
          parentId: edge.source
        }
      };

      // Create new edges for React Flow using consistent "edge-<source>-<target>" pattern
      const sourceEdge = {
        id: `edge-${edge.source}-${newNodeId}`,
        source: edge.source,
        target: newNodeId,
        type: 'workflow'
      };

      const targetEdge = {
        id: `edge-${newNodeId}-${edge.target}`,
        source: newNodeId,
        target: edge.target,
        type: 'workflow'
      };

      // Update edges - remove the clicked edge and add the two new edges
      setEdges(edges => 
        edges.filter(e => e.id !== edgeId).concat([sourceEdge, targetEdge])
      );

      // Add the new node to React Flow AND update parentId of target node
      setNodes(nodes => {
        const updatedNodes = nodes.map(node => {
          if (node.id === edge.target) {
            return {
              ...node,
              data: {
                ...node.data,
                parentId: newNodeId,
                _layoutTrigger: Date.now() // force relayout if needed
              },
              selected: false // Deselect other nodes
            };
          }
          return {
            ...node,
            selected: false // Deselect other nodes
          };
        });
        
        // Add the new node as selected
        return [...updatedNodes, { ...newNode, selected: true }];
      });
      
      // Center view on the new node
      setTimeout(() => {
        setCenter(position.x, position.y, { zoom: 1, duration: 300 });
      }, 100);
      
      console.log('✅ Successfully inserted new node:', newNodeId, 'between', edge.source, 'and', edge.target);
      
    } catch (error) {
      console.error('Failed to insert node between edges:', error);
    }
  };

  return handleEdgeClick;
}

export default useEdgeClick; 